import { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { openContact } from "./ContactDialog";

const PACKAGES = [
  {
    id: "foundation",
    pkg: "Package 1",
    name: "The Foundation",
    price: 200,
    bg: "bg-staxyellow",
    text: "text-ink",
    features: ["Custom Logo", "Color & Font Guide"],
    blurb: "A fresh, professional look — custom logo, your colors, the right fonts. The easiest way to stand out to your clients.",
  },
  {
    id: "growth",
    pkg: "Package 2",
    name: "Growth",
    price: 400,
    bg: "bg-staxcyan",
    text: "text-ink",
    pop: true,
    features: ["Everything in The Foundation", "Business Card Design (Qty 100)", "Service Menu Design (Qty 25)", "Instagram Templates"],
    blurb: "Your new look, ready for the spotlight — marketing pieces and social posts that match your brand perfectly, everywhere clients find you.",
  },
  {
    id: "scalable",
    pkg: "Package 3",
    name: "Scalable Suite",
    price: 750,
    bg: "bg-staxmagenta",
    text: "text-white",
    features: ["Everything in Growth", "Brand Launch Kit", "3 Page Website"],
    blurb: "The total transformation — a professional 3-page website plus a launch kit to show off your new brand from day one.",
  },
];

const BYO_ITEMS = [
  { id: "logo", name: "Custom Logo", price: 150 },
  { id: "color-font", name: "Color & Font Guide", price: 50 },
  { id: "biz-cards", name: "Business Card Design (Qty 100)", price: 75 },
  { id: "service-menu", name: "Service Menu Design (Qty 25)", price: 50 },
  { id: "ig-templates", name: "Instagram Templates", price: 75 },
  { id: "launch-kit", name: "Brand Launch Kit", price: 50 },
  { id: "website", name: "3 Page Website", price: 300 },
];

export const BrandPackagesBody = ({ onAction }) => {
  const [byo, setByo] = useState([]);

  const toggle = (id) =>
    setByo((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const chosen = BYO_ITEMS.filter((i) => byo.includes(i.id));
  const total = chosen.reduce((sum, i) => sum + (i.price || 0), 0);
  const quotes = chosen.filter((i) => !i.price).length;

  const startByo = () => {
    const list = chosen.map((i) => `${i.name}${i.price ? ` ($${i.price})` : ""}`).join(", ");
    onAction?.();
    openContact(
      `Hi! I'd like to build my own branding package with: ${list}.${total ? ` Estimated total: $${total}.` : ""}${quotes ? " Some items need a custom quote." : ""}`
    );
  };

  return (
    <div data-testid="packages-section">
      <p className="max-w-2xl text-base font-medium text-ink/65 leading-relaxed">
        From "just another business" to a standout, high-performing brand. Whether you need a quick,
        professional refresh or a total transformation, these packages are built to help you grow —
        a visual identity that feels authentic to your craft and stays consistent on every platform.
      </p>

      <div className="mt-8 border-4 border-ink shadow-hard">
        <div className="hidden lg:grid grid-cols-12 border-b-4 border-ink bg-staxcyan">
          <span className="col-span-4 px-6 py-3 font-display uppercase text-sm tracking-wide text-ink">Tier</span>
          <span className="col-span-2 px-6 py-3 font-display uppercase text-sm tracking-wide text-ink border-l-4 border-ink">Rate</span>
          <span className="col-span-6 px-6 py-3 font-display uppercase text-sm tracking-wide text-ink border-l-4 border-ink">What's Included</span>
        </div>
        {PACKAGES.map((p, i) => (
          <div
            key={p.id}
            data-testid={`package-${p.id}`}
            className={`grid grid-cols-1 lg:grid-cols-12 ${i < PACKAGES.length - 1 ? "border-b-4 border-ink" : ""}`}
          >
            <div className={`lg:col-span-4 ${p.bg} ${p.text} px-6 py-8 flex flex-col justify-center relative`}>
              {p.pop && (
                <span className="absolute top-4 right-4 border-2 border-ink bg-ink px-3 py-1 font-mono2 text-[9px] font-bold uppercase tracking-[0.25em] text-staxyellow">
                  Most Popular
                </span>
              )}
              <p className="font-mono2 text-xs font-bold uppercase tracking-[0.3em] opacity-60">{p.pkg}</p>
              <h3 className="mt-1 font-display uppercase text-3xl leading-none">{p.name}</h3>
              <p className="mt-3 text-sm font-medium opacity-80 leading-relaxed">{p.blurb}</p>
            </div>
            <div className="lg:col-span-2 bg-white px-6 py-8 flex flex-col justify-center items-start lg:items-center border-t-4 lg:border-t-0 lg:border-l-4 border-ink">
              <p className="font-display text-5xl text-ink">${p.price}</p>
              <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/40 mt-1">One time</p>
            </div>
            <div className="lg:col-span-6 bg-white px-6 py-8 border-t-4 lg:border-t-0 lg:border-l-4 border-ink flex flex-col justify-between gap-6">
              <ul className="space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm font-semibold text-ink">
                    <Check size={17} className="mt-0.5 shrink-0 text-staxmagenta" strokeWidth={3} /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`package-cta-${p.id}`}
                onClick={() => { onAction?.(); openContact(`Hi! I'd like to start with the ${p.name} branding package ($${p.price}).`); }}
                className="group inline-flex w-fit items-center gap-2 border-2 border-ink bg-ink px-6 py-3 font-display uppercase text-sm text-white transition-transform hover:-translate-y-0.5"
              >
                Start With {p.name} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div data-testid="byo-package" className="mt-10 border-4 border-ink bg-paper text-ink shadow-hard">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b-4 border-ink bg-staxyellow px-6 py-4">
          <div>
            <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.3em] text-ink/60">Package 4</p>
            <h3 className="font-display uppercase text-2xl sm:text-3xl text-ink">Build Your Own</h3>
          </div>
          <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">Pick only what you need</p>
        </div>
        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {BYO_ITEMS.map((i) => {
              const on = byo.includes(i.id);
              return (
                <button
                  key={i.id}
                  data-testid={`byo-item-${i.id}`}
                  onClick={() => toggle(i.id)}
                  className={`flex items-center justify-between gap-3 border-2 px-4 py-3.5 text-left transition-all ${
                    on ? "border-ink bg-staxmagenta -translate-y-0.5 shadow-hard-sm" : "border-ink/25 bg-white hover:border-ink"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 ${on ? "border-ink bg-white" : "border-ink/40"}`}>
                      {on && <Check size={12} className="text-ink" strokeWidth={4} />}
                    </span>
                    <span className={`text-sm font-semibold ${on ? "text-white" : "text-ink"}`}>{i.name}</span>
                  </span>
                  <span className={`font-mono2 text-xs font-bold ${on ? "text-white" : "text-ink/60"}`}>
                    {i.price ? `$${i.price}` : "QUOTE"}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-5 border-t-2 border-ink/15 pt-6">
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-ink/50">
                {chosen.length ? `${chosen.length} item${chosen.length > 1 ? "s" : ""} selected` : "Nothing selected yet"}
                {quotes > 0 ? " · some custom-quoted" : ""}
              </p>
              <p data-testid="byo-total" className="mt-1 font-display text-5xl text-ink">
                ${total}
              </p>
            </div>
            <button
              data-testid="byo-cta"
              onClick={startByo}
              disabled={!chosen.length}
              className="group inline-flex items-center gap-2 border-2 border-ink bg-staxcyan px-8 py-4 font-display uppercase text-lg text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
            >
              Start My Custom Package <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
